import { faArrowRight, faArrowsRepeat } from "@fortawesome/pro-regular-svg-icons";
import { faCheckDouble, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";

import { ReviewRating } from "common/types";
import { parseNumber } from "common/utils/numbers";
import LinkButton from "common/components/LinkButton";
import {
  OrderItemModificationType,
  OrderItemStatus,
  ApprovalNeededReason,
} from "common/types/orders";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import ReviewStarsInput from "client/reviews/components/ReviewStarsInput";
import TextChip from "client/common/components/content/TextChip";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import { urls } from "client/routes";
import Banner from "client/common/components/content/Banner";
import { sendSelectItem } from "client/analytics/utils";

import { ClientListOrder, ClientListOrderItem } from "../types";
import OrderItemTimeline from "./OrderItemTimeline";

import styles from "../styles/OrderItem.module.less";

type OrderItemModificationProps = {
  type: OrderItemModificationType;
  onOpenModifications: () => void;
};

const OrderItemModification = ({ type, onOpenModifications }: OrderItemModificationProps) => {
  const typeToLabel: Record<OrderItemModificationType, string> = {
    [OrderItemModificationType.Create]: "This item was added",
    [OrderItemModificationType.Update]: "This item was modified",
    [OrderItemModificationType.Delete]: "This item was canceled",
  };

  return (
    <Banner.LinkButton
      variant="primary"
      className={styles.banner}
      inline={true}
      onClick={onOpenModifications}
    >
      <Banner.Text label={typeToLabel[type]} />
    </Banner.LinkButton>
  );
};
type OrderItemProps = {
  order: ClientListOrder;
  orderItem: ClientListOrderItem;
  onOpenReview: (rating?: ReviewRating) => void;
  onOpenModifications: () => void;
  classNames?: {
    container?: string;
    info?: string;
    related?: string;
    item?: string;
    content?: string;
    image?: string;
    timeline?: string;
  };
};

const OrderItem = ({
  order,
  orderItem,
  onOpenReview,
  onOpenModifications,
  classNames,
}: OrderItemProps) => {
  const itemUrl = urls.products.detail({
    params: { id: orderItem.item.product.product_line.id },
    search: {
      item: orderItem.item.id,
      product: orderItem.item.product.id,
    },
  });

  const handleOpenReview = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Casting because while the target could be anything, we know internally it should
    // be an HTMLElement. But just in case, let's use optional chaining on the next values
    const target = event.target as HTMLElement;
    const rating = parseNumber(target?.dataset?.rating) as ReviewRating;

    onOpenReview(rating || undefined);
  };

  const needsHighRiskConfirmation =
    orderItem.status === OrderItemStatus.ApprovalNeeded &&
    orderItem.approval_needed_reason === ApprovalNeededReason.HIGH_RISK_CLIENT;

  return (
    <div className={cx(styles.container, classNames?.container)}>
      <div className={styles.itemColumn}>
        {orderItem.modification_type && (
          <OrderItemModification
            type={orderItem.modification_type}
            onOpenModifications={onOpenModifications}
          />
        )}
        <div className={cx(styles.item, classNames?.item)}>
          <CatalogImage.Link
            dest={itemUrl}
            image={orderItem.item.image}
            size="medium"
            alt={orderItem.item.full_name}
            className={cx(styles.image, classNames?.image)}
            onClick={() => sendSelectItem(orderItem.item, "Order Item", orderItem.unit_price)}
          />
          <div className={cx(styles.content, classNames?.content)}>
            <div>
              <LinkButton
                dest={itemUrl}
                className={styles.name}
                onClick={() => sendSelectItem(orderItem.item, "Order Item", orderItem.unit_price)}
              >
                {orderItem.item.product.product_line.name}
              </LinkButton>
              <p className={styles.description}>
                {orderItem.item.product.name} · {orderItem.item.name}
                {orderItem.flavors_display && ` · ${orderItem.flavors_display}`} · Qty{" "}
                {orderItem.quantity}
              </p>
              {orderItem.patient && (
                <p className={styles.description}>Ordered for {orderItem.patient.name_display}</p>
              )}
            </div>
            <div>
              {orderItem.item.product.product_line.prescription_required &&
                (orderItem.prescription_fill?.is_approved ? (
                  <TextChip>
                    <Icon icon={faCheckDouble} /> Active prescription on file
                  </TextChip>
                ) : (
                  <TextChip>
                    <Icon icon={faExclamationTriangle} /> Awaiting Prescription
                  </TextChip>
                ))}
              {needsHighRiskConfirmation && (
                <TextChip>
                  <Icon icon={faExclamationTriangle} /> Pending approval
                </TextChip>
              )}
            </div>
            <CrossedPrice
              crossed={orderItem.subtotal}
              price={orderItem.subtotal - orderItem.amortized_discount}
            />
            <p className={cx(styles.info, classNames?.info)}>
              {orderItem.item.product.product_line.short_description}
            </p>
            <div className={cx(styles.related, classNames?.related)}>
              {orderItem.subscription && (
                <LinkButton
                  dest={urls.subscriptions.detail({ params: { id: orderItem.subscription.id } })}
                  className={cx(styles.button, styles.selectable)}
                >
                  <Icon icon={faArrowsRepeat} fixedWidth={true} />
                  <p>Ships {orderItem.subscription.frequency_display.toLowerCase()}</p>
                  <Icon className={styles.arrow} icon={faArrowRight} fixedWidth={true} />
                </LinkButton>
              )}
              {orderItem.reviews.length ? (
                <div className={styles.button}>
                  <p>Your rating</p>
                  <ReviewStarsInput isStatic={true} value={orderItem.reviews[0].rating} size="s" />
                </div>
              ) : (
                <button
                  className={cx(styles.button, styles.selectable)}
                  type="button"
                  onClick={handleOpenReview}
                >
                  <p>Write a review</p>
                  <ReviewStarsInput
                    size="s"
                    renderReviewStar={({ star, onChange, ...other }) => (
                      <div {...other}>{star}</div>
                    )}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={cx(styles.timeline, classNames?.timeline)}>
        <OrderItemTimeline orderItem={orderItem} order={order} />
      </div>
    </div>
  );
};

export default OrderItem;
